import { logCustomDatadogAction } from "app/scripts/shared_setup"

const genericError = "There was a problem submitting your payment"

export default function SubscriptionHelper({ subscriptionsPath, planId, productId }) {
  /**
   * @public
   * @param {{billing_details: {name: String, email: String}, card: StripeCardElement}|PaymentMethod} paymentMethodData
   * @param promoCode
   * @param price
   * @param onSuccess
   * @param onError
   */
  async function subscribe({ paymentMethod, promoCode, priceId, onRequireAction, onSuccess, onError }) {
    await fetch(subscriptionsPath, {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": window.App.authenticityToken,
      },
      body: JSON.stringify({
        plan_id: planId,
        product_id: productId,
        payment_method: paymentMethod.id,
        promo_code: promoCode,
        price_id: priceId,
      }),
      redirect: "error",
    })
      .then(async result => {
        const payload = await result.json().catch(() => ({}))
        return { payload, status: result.status, ok: result.ok }
      })
      .then(async ({ payload, status, ok }) => {
        const { error, errors, subscription, permissions } = payload
        const subscriptionError = error || (errors && errors[0]) || (!ok && { message: `Checkout failed (${status})` })
        const logDefaultParams = { entrypoint: "checkout", subscription_status: subscription?.status || "unknown" }

        if (subscriptionError) {
          onError(subscriptionError)
        } else if (subscription?.pending_setup_intent_client_secret) {
          const logParams = {
            ...logDefaultParams,
            csa: "setup_intent",
          }
          logCustomDatadogAction("stripe_3ds_initiated", logParams)
          try {
            await onRequireAction(subscription.pending_setup_intent_client_secret, subscription.subscription_id)
            logCustomDatadogAction("stripe_3ds_confirmed", logParams)
          } catch (requireActionError) {
            logCustomDatadogAction("stripe_3ds_failed", {
              ...logParams,
              error_code: requireActionError?.code || requireActionError?.message || "unknown",
            })
            // User is already trialing. Not raise error because Stripe will send notification to confirm the payment before trial finishes.
          }

          onSuccess(subscription, permissions, planId, promoCode)
        } else if (subscription?.status === "incomplete") {
          if (!subscription?.payment_intent_client_secret) {
            onError({
              message:
                "Subscription requires additional authentication, but no payment details were returned. Try again.",
            })
          } else {
            const logParams = {
              ...logDefaultParams,
              csa: "payment_intent",
            }
            logCustomDatadogAction("stripe_3ds_initiated", logParams)
            try {
              await onRequireAction(subscription.payment_intent_client_secret, subscription.subscription_id)
              logCustomDatadogAction("stripe_3ds_confirmed", logParams)
              onSuccess(subscription, permissions, planId, promoCode)
            } catch (requireActionError) {
              logCustomDatadogAction("stripe_3ds_failed", {
                ...logParams,
                error_code: requireActionError?.code || requireActionError?.message || "unknown",
              })
              onError(requireActionError)
            }
          }
        } else if (subscription?.status === "active" || subscription?.status === "trialing") {
          onSuccess(subscription, permissions, planId, promoCode)
        }
      })
      .catch(unexpectedError => {
        if (window.App.allowDebugErrorMessages) console.error(unexpectedError)

        if (window.App?.Sentry?.captureException) {
          const err =
            unexpectedError instanceof Error
              ? unexpectedError
              : new Error(typeof unexpectedError === "string" ? unexpectedError : "subscribe fetch failed")
          window.App.Sentry.captureException(err, {
            tags: { product_area: "subscriptions", checkout: "subscribe_fetch" },
            extra: { raw: unexpectedError },
          })
        }

        onError(genericError)
      })
  }

  return { subscribe }
}
